import { useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";

export const RecaptchaSettings = () => {
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ mode: "off", v2_site_key: "", v2_secret_key: "", v3_site_key: "", v3_secret_key: "", v3_threshold: 0.5 });
  const [busy, setBusy] = useState(false);
  const H = () => ({ headers: { "X-Master-Pin": pin } });

  const unlock = async () => {
    try {
      const { data } = await api.get("/recaptcha/admin", H());
      setCfg(data);
      setForm((f) => ({ ...f, mode: data.mode, v2_site_key: data.v2_site_key, v3_site_key: data.v3_site_key, v3_threshold: data.v3_threshold }));
      setUnlocked(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.v2_secret_key) delete body.v2_secret_key;
      if (!body.v3_secret_key) delete body.v3_secret_key;
      await api.post("/recaptcha/admin", body, H());
      toast.success(form.mode === "off" ? "reCAPTCHA desactivado" : `reCAPTCHA activado en modo ${form.mode} (claves validadas con Google)`);
      unlock();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!unlocked) {
    return (
      <div className="bg-white border border-neutral-200 rounded-2xl p-6 max-w-md" data-testid="recaptcha-pin-gate">
        <h3 className="font-semibold flex items-center gap-2 mb-2"><KeyRound size={16} /> Seguridad · reCAPTCHA</h3>
        <p className="text-sm text-neutral-500 mb-3">Introduce el PIN Maestro para gestionar las claves.</p>
        <div className="flex gap-2">
          <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN Maestro"
                 data-testid="recaptcha-pin-input" className="max-w-[160px]" />
          <Button onClick={unlock} data-testid="recaptcha-unlock-btn" className="rounded-full bg-neutral-900 text-white">Desbloquear</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 max-w-2xl space-y-4" data-testid="recaptcha-settings">
      <h3 className="font-semibold flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-600" /> reCAPTCHA del formulario de acceso</h3>
      <div>
        <Label>Modo</Label>
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
                data-testid="recaptcha-mode-select"
                className="mt-1 h-10 w-full rounded-md border border-neutral-300 px-3 text-sm">
          <option value="off">Desactivado</option>
          <option value="v2">Solo v2 (casilla "No soy un robot")</option>
          <option value="v3">Solo v3 (invisible, por puntuación)</option>
          <option value="both">Ambos (v2 + v3)</option>
        </select>
        <p className="text-[11px] text-neutral-500 mt-1">Solo se activa si Google valida las claves secretas. Si la validación falla, se conserva la configuración anterior.</p>
      </div>
      {(form.mode === "v2" || form.mode === "both") && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>v2 · Site key</Label><Input value={form.v2_site_key} data-testid="recaptcha-v2-site" onChange={(e) => setForm({ ...form, v2_site_key: e.target.value })} className="mt-1" /></div>
          <div><Label>v2 · Secret key {cfg?.v2_secret_masked && <span className="text-neutral-400">({cfg.v2_secret_masked})</span>}</Label>
            <Input type="password" value={form.v2_secret_key} data-testid="recaptcha-v2-secret" placeholder="Nueva secret (vacío = mantener)" onChange={(e) => setForm({ ...form, v2_secret_key: e.target.value })} className="mt-1" /></div>
        </div>
      )}
      {(form.mode === "v3" || form.mode === "both") && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>v3 · Site key</Label><Input value={form.v3_site_key} data-testid="recaptcha-v3-site" onChange={(e) => setForm({ ...form, v3_site_key: e.target.value })} className="mt-1" /></div>
          <div><Label>v3 · Secret key {cfg?.v3_secret_masked && <span className="text-neutral-400">({cfg.v3_secret_masked})</span>}</Label>
            <Input type="password" value={form.v3_secret_key} data-testid="recaptcha-v3-secret" placeholder="Nueva secret (vacío = mantener)" onChange={(e) => setForm({ ...form, v3_secret_key: e.target.value })} className="mt-1" /></div>
        </div>
      )}
      {(form.mode === "v3" || form.mode === "both") && (
        <div><Label>Umbral v3 (0-1)</Label>
          <Input type="number" step="0.1" min="0" max="1" value={form.v3_threshold} data-testid="recaptcha-threshold"
                 onChange={(e) => setForm({ ...form, v3_threshold: parseFloat(e.target.value) || 0.5 })} className="mt-1 max-w-[120px]" /></div>
      )}
      <Button onClick={save} disabled={busy} data-testid="recaptcha-save-btn" className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
        {busy ? "Validando con Google…" : "Guardar y validar claves"}
      </Button>
    </div>
  );
};
